import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useRef } from "react";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { CATEGORIES } from "@/lib/blog";
import { formatDateTimeBR } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";
import { listPublishedPosts, listPublishedVideos, listActiveServices } from "@/lib/content.functions";
import { extractYouTubeId } from "@/lib/internal-links";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog SVG Multimídia | SEO, Google e Crescimento Orgânico" },
      { name: "description", content: "Artigos sobre SEO, Google Meu Negócio, criação de sites e estratégias para empresas crescerem organicamente no Google." },
      { property: "og:title", content: "Blog SVG Multimídia" },
      { property: "og:description", content: "Conteúdos sobre SEO, Google e crescimento orgânico." },
      { property: "og:url", content: "/blog" },
    ],
    links: [{ rel: "canonical", href: "https://www.svgmultimidia.com.br/blog" }],
  }),
  component: Blog,
});

function Blog() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("Todos");
  const [visibleCount, setVisibleCount] = useState(12);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { data: dbPosts = [] } = useQuery({
    queryKey: ["public-posts"],
    queryFn: () => listPublishedPosts(),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
  const { data: videos = [] } = useQuery({
    queryKey: ["public-videos"],
    queryFn: () => listPublishedVideos(),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  });
  const { data: services = [] } = useQuery({
    queryKey: ["public-services"],
    queryFn: () => listActiveServices(),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  });
  const dynamicCategories = useMemo(() => {
    const fromPosts = new Set(dbPosts.map((p: any) => p.category).filter(Boolean));
    const fromServices = services.map((s: any) => s.label);
    const merged = new Set<string>([...fromServices, ...Array.from(fromPosts), ...CATEGORIES]);
    return Array.from(merged);
  }, [services, dbPosts]);

  const allPosts = useMemo(() => {
    return dbPosts.map((p) => ({
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt || p.description,
      category: p.category,
      cover: p.cover,
      coverImage: p.cover_image,
      date: p.published_at ?? new Date().toISOString(),
      readingTime: p.reading_time,
    }));
  }, [dbPosts]);

  const filtered = useMemo(() => allPosts.filter((p) => {
    const matchQ = q === "" || p.title.toLowerCase().includes(q.toLowerCase()) || p.excerpt.toLowerCase().includes(q.toLowerCase());
    const matchC = cat === "Todos" || p.category === cat;
    return matchQ && matchC;
  }), [q, cat, allPosts]);

  // Reset visible window when filters change
  useEffect(() => { setVisibleCount(12); }, [q, cat]);

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisibleCount((c) => (c < filtered.length ? c + 12 : c));
      }
    }, { rootMargin: "600px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [filtered.length]);

  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Blog" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Conteúdo</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl"><span className="text-gradient">Blog SVG Multimídia</span></h1>
            <p className="mt-4 text-muted-foreground md:text-lg">Aprenda como melhorar sua presença digital e transformar buscas em oportunidades.</p>
          </div>
        </div>
      </section>

      <Section>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar artigos..."
            aria-label="Buscar artigos"
            className="w-full rounded-lg border border-border bg-background/60 px-4 py-3 text-sm md:max-w-sm focus:border-primary focus:outline-none"
          />
          <div className="flex flex-wrap gap-2">
            {["Todos", ...dynamicCategories].map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${cat === c ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary text-muted-foreground hover:text-foreground"}`}
              >{c}</button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((p, idx) => (
            <Reveal key={p.slug} delay={(idx % 3) as 0 | 1 | 2}>
              <Tilt3D>
                <Link to="/blog/$slug" params={{ slug: p.slug }} className="card-glass flex flex-col">
                  {p.coverImage ? (
                    <img
                      src={p.coverImage}
                      alt={p.title}
                      loading="lazy"
                      className="mb-4 h-40 w-full rounded-xl object-cover"
                    />
                  ) : (
                    <div className="mb-4 h-40 rounded-xl" style={{ background: p.cover }} />
                  )}
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">{p.category}</span>
                  <h2 className="mt-2 text-lg font-semibold leading-snug">{p.title}</h2>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatDateTimeBR(p.date)}</span>
                    <span>{p.readingTime}</span>
                  </div>
                </Link>
              </Tilt3D>
            </Reveal>
          ))}
          {filtered.length === 0 && (
            <p className="text-muted-foreground">Nenhum artigo encontrado.</p>
          )}
        </div>
        {visibleCount < filtered.length && (
          <div ref={sentinelRef} className="mt-8 flex justify-center py-6 text-sm text-muted-foreground">
            Carregando mais artigos…
          </div>
        )}
      </Section>

      {videos.length > 0 && (
        <Section className="bg-[color:var(--color-sidebar)]">
          <div className="max-w-3xl">
            <span className="eyebrow">Vídeos</span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl">Conteúdos em vídeo da SVG</h2>
            <p className="mt-3 text-muted-foreground">Vídeos selecionados pela equipe para aprofundar os temas tratados no blog.</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {videos.map((v, idx) => {
              const id = extractYouTubeId(v.url);
              return (
                <Reveal key={v.id} delay={(idx % 3) as 0 | 1 | 2}>
                  <Tilt3D>
                    <article className="card-glass flex h-full flex-col overflow-hidden p-0">
                      <div className="relative aspect-video w-full bg-black">
                        {id ? (
                          <iframe
                            src={`https://www.youtube.com/embed/${id}`}
                            title={v.title}
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="absolute inset-0 h-full w-full"
                          />
                        ) : (
                          <a href={v.url} target="_blank" rel="noopener" className="absolute inset-0 grid place-items-center text-sm text-primary">
                            Abrir vídeo →
                          </a>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <h3 className="text-base font-semibold leading-snug">{v.title}</h3>
                        {v.description && <p className="mt-2 text-sm text-muted-foreground">{v.description}</p>}
                      </div>
                    </article>
                  </Tilt3D>
                </Reveal>
              );
            })}
          </div>
        </Section>
      )}

    </>
  );
}